import {
  colors,
  IconButton,
  Table,
  Tooltip,
  type TableProps,
} from '@dev-spendesk/grapes';
import cx from 'classnames';
import React from 'react';

import { EllipsisTooltip } from 'common/components/EllipsisTooltip';
import {
  useTranslation,
  type TGlobalFunctionTyped,
} from 'common/hooks/useTranslation';
import type { CostCenter } from 'modules/budgets/models/costCenter';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';
import { useGetSyncOperationStatus } from 'src/core/modules/accounting-integration/apis/useGetSyncOperation';
import { type SyncMode } from 'src/core/modules/accounting-integration/apis/useSetSyncSettings';
import { useHasMapSync } from 'src/core/modules/accounting-integration/hooks/useHasMapSync';
import { type AnalyticalFieldMappingV2 } from 'src/core/modules/bookkeep';

import { MappedValue } from '../../../cost-centers/components/CostCentersPage/MappedValue';
import { type ExpenseCategory } from '../../expenseCategory';

const getCostCentersString = (
  costCenterIds: string[],
  costCenters: CostCenter[],
  t: TGlobalFunctionTyped,
) => {
  const costCentersList = costCenterIds
    .map((costCenterId) => {
      return (
        // Case where the cost center list does not have one of the cost center defined in the expense category associated list
        costCenters.find((costCenter) => costCenter.id === costCenterId)
          ?.name ?? undefined
      );
    })
    .filter((name): name is string => name !== undefined);
  if (costCentersList.length > 0) {
    return <EllipsisTooltip text={costCentersList.join(', ')} />;
  }

  return t('expenseCategories.appliedOnAllCostCenters');
};

type Props = {
  costCenters: CostCenter[];
  expenseCategories: ExpenseCategory[];
  externalValues?: Map<string, string | undefined>;
  isCostCentersFeatureEnabled: boolean;
  maxHeight?: number;
  onDelete: (expenseCategory: ExpenseCategory) => void;
  onEdit: (expenseCategory: ExpenseCategory) => void;
  rowHeight?: TableProps<ExpenseCategory>['rowHeight'];
  withBorder?: boolean;
  mapping?: AnalyticalFieldMappingV2 | undefined;
  selectedExpenseCategories?: ExpenseCategory[];
  onSelectionChange?: (selected: ExpenseCategory[]) => void;
  syncMode?: SyncMode;
};

export const ExpenseCategoryTable = ({
  costCenters,
  expenseCategories,
  externalValues,
  isCostCentersFeatureEnabled,
  maxHeight,
  onDelete,
  onEdit,
  mapping,
  rowHeight = 'compact',
  withBorder = false,
  selectedExpenseCategories = [],
  onSelectionChange,
  syncMode,
}: Props) => {
  const { t } = useTranslation('global');
  const isMapSyncEnabled = useHasMapSync();
  const { accountingSoftwareName } = useAccountingSoftwareName();

  const { syncOperation } = useGetSyncOperationStatus('pull');

  const getRowActions = (expenseCategory: ExpenseCategory) => {
    return (
      <div className="EcContainer__row__actions flex shrink-0 items-center justify-end gap-4 opacity-0 transition-opacity group-hover:opacity-100">
        <IconButton
          variant="tertiaryNeutral"
          iconName="pen"
          iconColor={colors.contentPrimary}
          onClick={() => onEdit(expenseCategory)}
          aria-label={t('misc.edit')}
        />

        {syncMode === 'pull' ? (
          <Tooltip
            content={t(
              'bookkeep.integrations.settings.analyticalFieldMapping.disableDeleteTooltip',
              { integration: accountingSoftwareName },
            )}
          >
            <IconButton
              variant="tertiaryNeutral"
              iconName="trash"
              iconColor={colors.contentPrimary}
              onClick={() => onDelete(expenseCategory)}
              aria-label={t('misc.delete')}
              disabled
            />
          </Tooltip>
        ) : (
          <IconButton
            variant="tertiaryNeutral"
            iconName="trash"
            iconColor={colors.contentPrimary}
            onClick={() => onDelete(expenseCategory)}
            aria-label={t('misc.delete')}
          />
        )}
      </div>
    );
  };

  const getColumns = () => {
    const columns = [
      {
        id: 'expenseCategory',
        header: t('expenseCategories.expenseCategory'),
        renderCell: (expenseCategory: ExpenseCategory) => (
          <div className="group flex grid-flow-col items-center justify-between">
            <div className="truncate">{expenseCategory.name}</div>
            {!isCostCentersFeatureEnabled && getRowActions(expenseCategory)}
          </div>
        ),
      },
    ];

    if (isMapSyncEnabled && mapping) {
      columns.push({
        id: 'externalValue',
        header: t('misc.integrationValue', {
          integration: accountingSoftwareName,
        }),
        renderCell: ({ id }: ExpenseCategory) => {
          const externalValue = externalValues?.get(id);
          return <MappedValue value={externalValue} />;
        },
      });
    }

    if (isCostCentersFeatureEnabled) {
      columns.push({
        id: 'associatedCostCenter',
        header: t('expenseCategories.associatedCostCenter'),
        renderCell: (expenseCategory: ExpenseCategory) => (
          <div className="group flex grid-flow-col items-center justify-between">
            <div className="truncate">
              {getCostCentersString(
                expenseCategory.costCenterIds,
                costCenters,
                t,
              )}
            </div>
            {getRowActions(expenseCategory)}
          </div>
        ),
      });
    }

    return columns;
  };

  const shouldShowPullingState =
    isMapSyncEnabled && syncOperation && syncOperation.status === 'in_progress';

  const selectionProps = onSelectionChange
    ? {
        selectedRowIds: selectedExpenseCategories.map((ec) => ec.id),
        onRowSelectionChange: (
          row: ExpenseCategory,
          id: string,
          checked: boolean,
        ) => {
          if (checked) {
            onSelectionChange([...selectedExpenseCategories, row]);
          } else {
            onSelectionChange(
              selectedExpenseCategories.filter((ec) => ec.id !== id),
            );
          }
        },
        onAllRowsSelectionChange: (
          rows: ExpenseCategory[],
          _: string[],
          checked: boolean,
        ) => {
          onSelectionChange(checked ? rows : []);
        },
      }
    : {};

  return (
    <Table
      className={cx('EcContainer__expenseCategoryTable', {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'EcContainer__expenseCategoryTable--noBorder': !withBorder,
      })}
      columns={getColumns()}
      data={expenseCategories}
      rowHeight={rowHeight}
      maxHeight={maxHeight}
      emptyState={
        shouldShowPullingState
          ? {
              title: t('expenseCategories.mapSync.emptyPullingTitle', {
                integration: accountingSoftwareName,
              }),
              subtitle: t('expenseCategories.mapSync.emptyPullingSubtitle'),
            }
          : {
              title: t('expenseCategories.emptyState.title'),
              subtitle: t('expenseCategories.emptyState.subtitle'),
            }
      }
      getRowId={(row) => row.id}
      {...selectionProps}
    />
  );
};
